import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { ProfileRouteProps } from "../../types/screensTypes";
import { User } from "../../types/types";
import { getAdminInfo, updateAdminInfo } from "../../api";
import HorizontalSeparator from "../../components/common/HorizontalSeparator";
import TextInput from "../../components/ui/TextInput";
import Button from "../../components/ui/Button";
import { uploadFileToS3Complete } from "../../api/s3";
import { FileInput } from "../../components/common/FileInput";
import ImageList from "../../components/images/ImageList";
import ImageSpecsBadges from "../../components/common/ImageSpecsBadges";
import { useImageValidation } from "../../hooks/useImageValidation";
import { validatePassword } from "../../utils/passwordValidation";
import { IMAGE_FORMAT, IMAGE_MAX_SIZE_300_KB } from "../../utils/image";
import styles from "./Profile.module.css";

const Profile: React.FC<ProfileRouteProps> = ({ handleLogout }) => {
    const [passwordVisible, setPasswordVisible] = useState<boolean>(false);
    const [user, setUser] = useState<User>({} as User);
    const [pictures, setPictures] = useState<string[]>([]);

    const [modifiedFields, setModifiedFields] = useState<Record<string, boolean>>({});
    const { t: rawT } = useTranslation();
    const t = rawT as any;
    const [, setLoading] = useState<boolean>(false);
    const [, setError] = useState<string>("");
    const [success, setSuccess] = useState<boolean>(false);
    const imageValidation = useImageValidation({
        type: "avatar",
        imageUrl: pictures[0] || undefined,
    });

    const togglePasswordVisibility = () => {
        setPasswordVisible((prev) => !prev);
    };

    const passwordValidation = useMemo(() => validatePassword(user.password || ""), [user.password]);
    const showPasswordRequirements = (user.password || "").length > 0;

    const fetchUser = async () => {
        setError("");
        const token = localStorage.getItem("token");
        if (!token) {
            setError("Something is wrong");
            setTimeout(() => setError(""), 3000);
            return;
        }
        setLoading(true);
        const response = await getAdminInfo({ token });
        console.log("response", response);
        setLoading(false);
        if (!response) {
            console.error("Error fetching user info:");
        } else {
            const userAPI = {
                ...response,
                company: response.cmpany?.name || "",
            };
            if (response.logo) {
                const pic = [response.logo];
                setPictures(pic || []);
            }
            setUser(userAPI);
        }
    };

    useEffect(() => {
        fetchUser();
    }, []);

    const disconnectUser = () => {
        localStorage.removeItem("token");
        handleLogout();
    };

    const handleFileSelected = async (file: File | File[]) => {
        if (Array.isArray(file)) {
            console.error("Multiple files not supported");
            return;
        }
        const token = localStorage.getItem("token");
        if (!token) {
            console.error("No authentication token found");
            return;
        }

        await imageValidation.validateImage(file);

        uploadFileToS3Complete(
            {
                file,
                isPublic: true, // Public files for general uploads
                isFolder: false, // No folder structure for general uploads
            },
            token,
        )
            .then((result: string) => {
                if (user !== undefined) {
                    const newUser = { ...user, logo: result };
                    setUser(newUser);
                    setPictures([result]);
                }

                setModifiedFields((prevFields) => ({ ...prevFields, logo: true }));
            })
            .catch((error) => console.error("Error uploading image:", error));
    };

    const handleInputChange = (name: string, value: string) => {
        if (user !== undefined) {
            const newUser = { ...user, [name]: value };
            setUser(newUser);
            setModifiedFields((prevFields) => ({
                ...prevFields,
                [name]: true,
            }));
        }
    };

    const handleSubmit = async () => {
        const updatedData: Partial<User> = {};

        (Object.keys(modifiedFields) as (keyof User)[]).forEach((field) => {
            if (modifiedFields[field] && user) {
                updatedData[field] = user[field] as any;
            }
        });

        try {
            const token = localStorage.getItem("token") || "";
            const response = await updateAdminInfo({ token, updatedData });

            if (!response) {
                console.error("Error updating profile");
            } else {
                setModifiedFields({});
                fetchUser();
                setSuccess(true);
                setTimeout(() => setSuccess(false), 3000);
            }
        } catch (error) {
            console.error("Error updating profile:", error);
        }
    };

    return (
        <div className={styles.page}>
            <div className={styles.sidebar}>
                <h2>{t("profile")}</h2>
                <ul>
                    <li>
                        <span>{t("editMyInformations")}</span>
                        <span>-</span>
                    </li>
                    <HorizontalSeparator />
                    <li className={styles.logoutLink} onClick={disconnectUser}>
                        {t("logout")}
                    </li>
                </ul>
            </div>

            <div className={styles.content}>
                <h2>{t("editMyInformations")}</h2>
                <form className={styles.form} onSubmit={(e) => e.preventDefault()}>
                    <TextInput
                        placeholder={t("companyName")}
                        value={user.company || ""}
                        onChange={(e) => handleInputChange("company", e.target.value)}
                    />
                    <TextInput
                        placeholder={t("email")}
                        value={user.email || ""}
                        onChange={(e) => handleInputChange("email", e.target.value)}
                    />
                    <div className={styles.passwordInput}>
                        <TextInput
                            type={passwordVisible ? "text" : "password"}
                            placeholder={t("password")}
                            value={user.password || ""}
                            onChange={(e) => handleInputChange("password", e.target.value)}
                        />
                        <button type="button" className={styles.passwordToggle} onClick={togglePasswordVisibility}>
                            {passwordVisible ? "🙈" : "👁️"}
                        </button>
                    </div>

                    {showPasswordRequirements && (
                        <div
                            className="password-requirements"
                            style={{ fontSize: "12px", marginTop: "-12px", marginBottom: "8px" }}
                        >
                            <p style={{ margin: "0 0 4px 0", fontWeight: 500 }}>{t("passwordRequirements")}:</p>
                            <ul style={{ margin: 0, paddingLeft: "20px", listStyle: "none" }}>
                                <li style={{ color: passwordValidation.requirements.minLength ? "green" : "red" }}>
                                    {passwordValidation.requirements.minLength ? "✓" : "✗"} {t("passwordMinLength")}
                                </li>
                                <li style={{ color: passwordValidation.requirements.hasUppercase ? "green" : "red" }}>
                                    {passwordValidation.requirements.hasUppercase ? "✓" : "✗"} {t("passwordUppercase")}
                                </li>
                                <li style={{ color: passwordValidation.requirements.hasLowercase ? "green" : "red" }}>
                                    {passwordValidation.requirements.hasLowercase ? "✓" : "✗"} {t("passwordLowercase")}
                                </li>
                                <li style={{ color: passwordValidation.requirements.hasNumber ? "green" : "red" }}>
                                    {passwordValidation.requirements.hasNumber ? "✓" : "✗"} {t("passwordNumber")}
                                </li>
                                <li style={{ color: passwordValidation.requirements.hasSpecial ? "green" : "red" }}>
                                    {passwordValidation.requirements.hasSpecial ? "✓" : "✗"} {t("passwordSpecial")}
                                </li>
                            </ul>
                        </div>
                    )}

                    <TextInput
                        placeholder={t("phoneNumber")}
                        value={user.phone || ""}
                        onChange={(e) => handleInputChange("phone", e.target.value)}
                    />

                    <div className={styles.fileUpload}>
                        <label>{t("insertYourLogo")}</label>
                        <ImageSpecsBadges spec="avatar" warnings={imageValidation.warnings} />
                        <FileInput
                            onFileSelected={handleFileSelected}
                            acceptedFileTypes={IMAGE_FORMAT}
                            maxSize={IMAGE_MAX_SIZE_300_KB}
                            maxSizeLabel={t("imageMaxSize300kb")}
                        />
                    </div>

                    {pictures && pictures.length > 0 && (
                        <div className={styles.photoGallery}>
                            <ImageList pictures={pictures} setPictures={setPictures} />
                        </div>
                    )}

                    <Button
                        label={success ? t("modifySuccess") : t("modify")}
                        variant="primary"
                        onClick={handleSubmit}
                        disabled={showPasswordRequirements && !passwordValidation.isValid}
                    />
                </form>
            </div>
        </div>
    );
};

export default Profile;
